import { useState, type JSX } from 'react';
import clsx from 'clsx';
import { toast } from 'sonner';
import { mapUnitSummaryToFormValues } from '@/lib/supabase/unitMappers';
import type { UnitSummary } from '@/lib/types/property';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import { useUnitUpdateMutation } from '@/hooks/useDashboardProperties';
import { formatUnitLabel } from '@/lib/utils/propertyFormatting';

interface UnitPublishToggleProps {
  propertyId: string;
  unit: UnitSummary;
  fullWidth?: boolean;
  size?: 'sm' | 'md';
}

export function UnitPublishToggle({
  propertyId,
  unit,
  fullWidth = false,
  size = 'md',
}: UnitPublishToggleProps): JSX.Element {
  const user = useDashboardUser();
  const mutation = useUnitUpdateMutation({ user, propertyId });
  const [isSaving, setIsSaving] = useState(false);

  const nextPublished = !unit.published;
  const label = unit.published ? 'Outreach: Yes' : 'Outreach: No';

  const handleToggle = (): void => {
    if (isSaving) {
      return;
    }

    const payload = mapUnitSummaryToFormValues(unit);
    payload.published = nextPublished;
    setIsSaving(true);

    mutation.mutate(
      { unit, values: payload },
      {
        onSuccess: () => {
          toast.success(
            nextPublished
              ? `${formatUnitLabel(unit)} will now send outreach emails on saved changes.`
              : `${formatUnitLabel(unit)} will no longer send outreach emails.`,
          );
        },
        onError: (error) => {
          toast.error(`Unable to update ${formatUnitLabel(unit)}.`, {
            description: error instanceof Error ? error.message : undefined,
          });
        },
        onSettled: () => {
          setIsSaving(false);
        },
      },
    );
  };

  return (
    <button
      type="button"
      aria-pressed={unit.published}
      onClick={handleToggle}
      disabled={isSaving}
      className={clsx(
        'inline-flex min-h-11 items-center justify-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 disabled:cursor-not-allowed disabled:opacity-60 md:min-h-0',
        fullWidth && 'w-full',
        size === 'sm' && 'md:px-3 md:py-1',
        size === 'md' && 'text-sm',
        unit.published
          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
          : 'border-surface-subtle bg-white text-text-primary/70',
        isSaving && 'opacity-70',
      )}
    >
      <span
        className={clsx('h-2 w-2 rounded-full', {
          'bg-emerald-500': unit.published,
          'bg-surface-muted': !unit.published,
        })}
        aria-hidden="true"
      />
      {isSaving ? 'Saving…' : label}
    </button>
  );
}
